/**
 * <announcement-bar> — pause control for the announcement marquee.
 *
 * The scrolling itself is pure CSS (assets/section-announcement-bar.css) and
 * only runs below 990px. This element owns the paused state and nothing else.
 *
 * Two controls, one state:
 * - the strip itself, for anyone with a touchscreen or a mouse
 * - a button that is invisible until focused, for the keyboard
 *
 * The button is what satisfies WCAG 2.2.2 (motion running past five seconds
 * needs a mechanism to stop it) — a tap target is not a keyboard control.
 *
 * Under `prefers-reduced-motion` the stylesheet turns the animation off and
 * hides the button, so nothing here needs a media query: toggling a class that
 * no longer drives an animation is harmless. Same reasoning at desktop widths.
 *
 * Every query is scoped to `this`, so the element stays independent of
 * anything else on the page. Mirrors assets/customer-photos.js deliberately —
 * one pause contract, two marquees.
 */
const ANNOUNCEMENT_PAUSED_CLASS = 'anew-announcement__bar--paused';

class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-marquee-toggle]');
    this.viewport = this.querySelector('.anew-announcement__viewport');

    const flip = () => this.setPaused(!this.classList.contains(ANNOUNCEMENT_PAUSED_CLASS));

    if (this.viewport) this.viewport.addEventListener('click', flip);
    if (this.toggle) this.toggle.addEventListener('click', flip);
  }

  setPaused(paused) {
    this.classList.toggle(ANNOUNCEMENT_PAUSED_CLASS, paused);
    if (!this.toggle) return;

    this.toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');

    const label = paused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel;
    if (label) this.toggle.setAttribute('aria-label', label);
  }
}

if (!customElements.get('announcement-bar')) {
  customElements.define('announcement-bar', AnnouncementBar);
}
